import { z } from 'zod';
import type { LlmModelId } from './llm-model.js';

// ── Agent registry contract ─────────────────────────────────────────────────
// The shared, framework-agnostic shape for a reusable AI "agent" (a labeled
// model + prompt + guardrails). The agent-binding / run-mode vocabularies mirror
// the model Concord locked in its integrations-library PRD so the two products
// can extract a shared package later — keep these values BYTE-IDENTICAL across
// repos; record any deliberate divergence in the PRD's Cross-repo reuse section.
//
// Note: an action carries `agents[]` (0..n, like sources/destinations) and
// `runModes` — they live on `ActionDef`, NOT on the agent. The agent is just
// the reusable brain (model + prompt + guardrails); an action with an empty
// `agents[]` has no AI. AI is a property (having agents), not a category.
// Multi-agent actions describe their agents as "working together" — never as
// an ordered/numbered sequence.

/** How one agent participates in an action: always runs, or user-toggleable. */
export const ACTION_AGENT_BINDINGS = ['required', 'optional'] as const;
export type ActionAgentBinding = (typeof ACTION_AGENT_BINDINGS)[number];

/** How an action / automation can be invoked. Orthogonal to AI. */
export const RUN_MODES = ['once', 'schedule', 'trigger'] as const;
export type RunMode = (typeof RUN_MODES)[number];

// How an action can be run by hand, independent of any schedule or trigger:
// 'config' = Run executes the saved setup; 'form' = Run opens a per-run input
// form; 'none' = not run from a saved action (it fires from a trigger, or runs
// from its own tool, like the lookups).
export const MANUAL_RUN_KINDS = ['config', 'form', 'none'] as const;
export type ManualRun = (typeof MANUAL_RUN_KINDS)[number];

/**
 * Symmetric I/O vocabulary. An action reads Sources[] and writes Destinations[].
 * A `project` is the first source type (integrations plug in later). Destinations
 * subsume the old "notify task" + "Then" step — `notify` is one type. Lives here
 * (the shared vocab module) so `actions.ts` + the catalog can't drift.
 */
export const SOURCE_TYPES = ['project', 'integration'] as const;
export type SourceType = (typeof SOURCE_TYPES)[number];
// At most one run per company within the window; 'occurrence' = every match
// runs (per event/signal), the rule notifications need.
export const ACTION_COOLDOWNS = [
  'occurrence',
  '15m',
  '1h',
  '24h',
  '7d',
] as const;
export type ActionCooldown = (typeof ACTION_COOLDOWNS)[number];

export const DESTINATION_TYPES = [
  'notify',
  'webhook',
  // A connection-backed integration destination (references a `connections` row
  // by id and delivers through that integration, e.g. a Clay Monitor webhook).
  'clay',
  'crm',
  'internal',
] as const;
export type DestinationType = (typeof DESTINATION_TYPES)[number];
export const NOTIFY_CHANNELS = [
  'slack',
  'email',
  'discord',
  'telegram',
] as const;
export type NotifyChannel = (typeof NOTIFY_CHANNELS)[number];

// The plan feature a notify channel or the webhook destination needs; email is free.
// One definition serves the backend save/delivery gates and the admin pickers.
export const DESTINATION_CHANNEL_FEATURE: Readonly<
  Record<string, 'alerts' | 'webhooks'>
> = {
  slack: 'alerts',
  telegram: 'alerts',
  discord: 'alerts',
  webhook: 'webhooks',
};

/**
 * The per-kind context an agent injects (icp = the Company Profile's ICP
 * narrative; summary = tone/format guidance). The org-wide AI-memory layer
 * that once backed these was removed in favor of per-action memory_override.
 */
export const AGENT_CONTEXT_KINDS = ['icp', 'summary'] as const;
export type AgentContextKind = (typeof AGENT_CONTEXT_KINDS)[number];

/** A reusable AI agent: a labeled model + prompt + guardrails. */
export type Agent = {
  /** Stable id, e.g. 'icp'. */
  key: string;
  /** UI label, e.g. 'ICP Analyst'. */
  label: string;
  /** One-line, user-facing description of what THIS agent does (not generic). */
  description?: string;
  /** Pinned managed model; undefined = resolved at runtime via model-selection. */
  model?: LlmModelId;
  /**
   * Which context kind this agent injects under its contextLabel, when any.
   * Omitted for agents that take no injected context body.
   */
  contextKind?: AgentContextKind;
  /** Human-readable hard rules, surfaced on the Agents page. */
  guardrails: string[];
  /** Whether this agent wants the org's default Company Profile injected. */
  recommendsProfile: boolean;
  /**
   * Tool keys (TOOL_CATALOG) this agent may invoke in its reasoning loop, e.g.
   * ['web_search']. An agent is the thing that reasons; a tool is a callable
   * capability (name + description + input schema) — tools never run on their
   * own. Omitted = the agent uses no tools.
   */
  tools?: string[];
};

// ── Agents list wire DTO (Logspot admin API; snake_case) ─────────────────────
// The read model the `Actions › Agents` page consumes: each agent plus the
// billable actions it powers. NOT part of the cross-repo `Agent` contract above
// — this is Logspot's response shape, served by `GET /api/admin/agents`.
export const agentActionRefSchema = z.object({
  key: z.string(),
  label: z.string(),
  // This agent's binding ON that action (required = always runs; optional =
  // user-toggleable via agent_overrides).
  agent_binding: z.enum(ACTION_AGENT_BINDINGS),
  run_modes: z.array(z.enum(RUN_MODES)),
});
export type AgentActionRef = z.infer<typeof agentActionRefSchema>;

export const agentCatalogEntrySchema = z.object({
  key: z.string(),
  label: z.string(),
  description: z.string(),
  model: z.string().nullable(),
  context_kind: z.string().nullable(),
  recommends_profile: z.boolean(),
  guardrails: z.array(z.string()),
  actions: z.array(agentActionRefSchema),
});
export type AgentCatalogEntry = z.infer<typeof agentCatalogEntrySchema>;

export const agentsListResponseSchema = z.object({
  agents: z.array(agentCatalogEntrySchema),
});
export type AgentsListResponse = z.infer<typeof agentsListResponseSchema>;

// ── Action catalog wire DTO (Browse Catalog) ─────────────────────────────────
// One entry per billable action. `agents` are the 0..n agents that power it
// (empty for lookups/templates); multi-agent actions present them as "working
// together", never as an ordered sequence. `tools` is the deduped effective
// tool set (the action's own direct tools ∪ every attached agent's tools).
// `requires_web_search` is DERIVED from that set; such actions are hidden from
// customers in the catalog when no web-search provider is configured (see
// web_search_available on the list response); super-admins always see them.

/** One agent attached to an action, resolved for the UI. */
export const actionAgentRefSchema = z.object({
  key: z.string(),
  label: z.string(),
  // What THIS agent does — shown under its name (never generic shared copy).
  description: z.string(),
  binding: z.enum(ACTION_AGENT_BINDINGS),
});
export type ActionAgentRef = z.infer<typeof actionAgentRefSchema>;

/** One tool in an action's effective tool set, resolved for the UI. */
export const actionToolRefSchema = z.object({
  key: z.string(),
  label: z.string(),
  description: z.string().optional(),
});
export type ActionToolRef = z.infer<typeof actionToolRefSchema>;

/**
 * Catalog list price (NOT org-resolved — promos / per-org overrides resolve at
 * run time). `data_credits` 'variable' = settled from actual LLM token usage.
 * Display is gated behind the `actionsCredits` feature flag client-side.
 */
export const actionCostSchema = z.object({
  actions: z.number(),
  data_credits: z.union([z.number(), z.literal('variable')]),
  // Credits-only preview for VARIABLE actions: the expected Data-Credit cost
  // plus the reserve ceiling (`max`), quoted at the org's default model and the
  // managed markup so it tracks what a run settles. Present ONLY for variable
  // actions; omitted for fixed ones (their `data_credits` is already exact).
  data_credits_estimate: z
    .object({ expected: z.number(), max: z.number() })
    .optional(),
});
export type ActionCost = z.infer<typeof actionCostSchema>;

export const actionCatalogEntrySchema = z.object({
  key: z.string(),
  label: z.string(),
  // Short descriptor under the title (icon-row balance), e.g. 'Company research'.
  subtitle: z.string().nullable(),
  description: z.string().nullable(),
  // "What You Get" deliverable chips for the detail view.
  what_you_get: z.array(z.string()),
  kind: z.string(), // lookup | ai | template
  agents: z.array(actionAgentRefSchema),
  tools: z.array(actionToolRefSchema),
  cost: actionCostSchema,
  run_modes: z.array(z.enum(RUN_MODES)),
  feature_key: z.string().optional(),
  project_only: z.boolean(),
  requires_web_search: z.boolean(),
  manual_run: z.enum(MANUAL_RUN_KINDS),
  default_cooldown: z.enum(ACTION_COOLDOWNS),
  // Which Source / Destination types this action accepts — drives the Set Up
  // pickers. `project` sources are the org/project scope today; destinations
  // default to none for tasks with only internal output.
  accepted_sources: z.array(z.enum(SOURCE_TYPES)),
  accepted_destinations: z.array(z.enum(DESTINATION_TYPES)),
  // Entity-maintenance verbs (profile.build, account.watch): present in the
  // response so saved instances resolve their entry (Your Actions rows, the
  // configure dialog), but filtered from the Browse Catalog grid.
  hidden_from_catalog: z.boolean().optional(),
});
export type ActionCatalogEntry = z.infer<typeof actionCatalogEntrySchema>;

export const actionCatalogListResponseSchema = z.object({
  actions: z.array(actionCatalogEntrySchema),
  // Whether an outbound web-search provider is configured (drives client-side
  // hiding of `requires_web_search` actions for non-super-admins).
  web_search_available: z.boolean(),
});
export type ActionCatalogListResponse = z.infer<
  typeof actionCatalogListResponseSchema
>;
